import { useState, useRef, useEffect, useCallback } from "react";
import { api } from "@/lib/api";

const MENTION_TRIGGER = /(?:^|\s)@([a-zA-Z0-9_]{1,30})$/;

/**
 * A textarea (or single-line input) with @username autocomplete.
 * Reusable for comments, the editor's letter, and DM composers.
 *
 * Props:
 *  - value, onChange (string) — controlled
 *  - placeholder, className, style, rows, multiline (default true), disabled
 *  - testId — base test id (input gets `${testId}-input`)
 */
export const MentionInput = ({
  value,
  onChange,
  placeholder,
  className = "input-belle",
  style = {},
  rows = 3,
  multiline = true,
  disabled = false,
  maxLength,
  testId = "mention-input",
}) => {
  const ref = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [query, setQuery] = useState(null); // null = closed; "" or "ce" etc. when open

  // search whenever the open query changes
  useEffect(() => {
    if (query === null) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/users", { params: { q: query, limit: 6 } });
        if (!cancelled) {
          setSuggestions(data || []);
          setActiveIdx(0);
        }
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    }, 120);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query]);

  const detectTrigger = useCallback((text, caret) => {
    const before = text.slice(0, caret);
    const m = before.match(MENTION_TRIGGER);
    return m ? m[1] : null;
  }, []);

  const handleChange = (e) => {
    const next = e.target.value;
    onChange(next);
    const caret = e.target.selectionStart ?? next.length;
    const q = detectTrigger(next, caret);
    setQuery(q);
  };

  const handleSelect = (u) => {
    const el = ref.current;
    if (!el) return;
    const caret = el.selectionStart ?? value.length;
    const before = value.slice(0, caret);
    const after = value.slice(caret);
    const replaced = before.replace(MENTION_TRIGGER, (full, _name) => {
      // preserve any leading whitespace (or empty if at start)
      const lead = full.startsWith("@") ? "" : full[0];
      return `${lead}@${u.username} `;
    });
    const newVal = replaced + after;
    onChange(newVal);
    setQuery(null);
    // place caret right after the inserted mention
    requestAnimationFrame(() => {
      if (!ref.current) return;
      const pos = replaced.length;
      ref.current.focus();
      ref.current.setSelectionRange(pos, pos);
    });
  };

  const handleKeyDown = (e) => {
    if (query === null || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      handleSelect(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setQuery(null);
    }
  };

  const Tag = multiline ? "textarea" : "input";

  return (
    <div style={{ position: "relative", flex: style.flex || 1, width: "100%" }}>
      <Tag
        ref={ref}
        data-testid={`${testId}-input`}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => setQuery(null), 150)}
        placeholder={placeholder}
        rows={multiline ? rows : undefined}
        disabled={disabled}
        maxLength={maxLength}
        className={className}
        style={{ width: "100%", ...style }}
      />
      {query !== null && suggestions.length > 0 && (
        <div
          data-testid={`${testId}-suggestions`}
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            marginTop: 4,
            background: "var(--surface)",
            border: "1px solid var(--border-default)",
            boxShadow: "0 12px 40px rgba(51,51,51,0.10)",
            minWidth: 240,
            maxWidth: 320,
            zIndex: 50,
            padding: "4px 0",
          }}
        >
          {suggestions.map((u, i) => (
            <button
              key={u.id}
              type="button"
              data-testid={`${testId}-suggestion-${u.username}`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(u);
              }}
              onMouseEnter={() => setActiveIdx(i)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                width: "100%",
                padding: "8px 14px",
                border: "none",
                cursor: "pointer",
                background: i === activeIdx ? "var(--bg-alt)" : "transparent",
                textAlign: "left",
              }}
            >
              {u.avatar_url ? (
                <img
                  src={u.avatar_url}
                  alt=""
                  style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                />
              ) : (
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: "var(--bg-alt)",
                    border: "1px solid var(--border-default)",
                    flexShrink: 0,
                  }}
                />
              )}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 15,
                    color: "var(--text-primary)",
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {u.display_name || u.username}
                </div>
                <div
                  className="overline"
                  style={{ fontSize: 10, marginTop: 1, color: "var(--text-muted)" }}
                >
                  @{u.username}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionInput;
